import { snapToPixel, lerp, clampRange, clamp01 } from '../util/math.js';
import { pointDistance, spotReached, movePointTowards } from '../util/interpolation.js';
import { fieldMetrics, orientX, screenXForYard } from '../field/metrics.js';
import { stopReasonForPlay, playHash } from './helpers.js';
import { clonePlayers, findPlayer, carriedBallPoint, puntCarryBallPoint, puntSnapBallPoint, setPlayerState, setPlayersStill } from '../player/model.js';
import { createPathSegment, createHoldSegment, evaluateTrack, compactPathPoints, createMotionState, steerMotionState, guidePointAt, flowingGuideTarget, projectedPoint, freezeLiveFrame, cloneLiveFrame, curvedApproachTarget } from './track.js';
import { liveFrameCount, puntHangTimeSeconds, puntOperationSeconds } from './context.js';
import { playerPixelsPerFrame } from './speed.js';
import type { FieldPlayer } from '../player/model.js';
import { buildArcadePuntLiveSequence } from './live-punt-arcade.js';

function seedMotionState(
  role: string,
  basePlayers: FieldPlayer[],
  tracks: Map<string, any[]>,
  frame: number,
): any | null {
  const basePlayer = findPlayer(basePlayers, role);
  if (!basePlayer) {
    return null;
  }
  const current = evaluateTrack(tracks.get(role) ?? null, frame, basePlayer);
  const previous = evaluateTrack(tracks.get(role) ?? null, Math.max(0, frame - 1), basePlayer);
  const state = createMotionState({ ...basePlayer, x: current.x, y: current.y });
  state.vx = current.x - previous.x;
  state.vy = current.y - previous.y;
  return state;
}

function blendPoints(a: any, b: any, amount: number) {
  return {
    x: snapToPixel(lerp(a.x, b.x, clamp01(amount))),
    y: snapToPixel(lerp(a.y, b.y, clamp01(amount))),
  };
}

function smoothPuntReturnerFrames(frames: any[], direction: number, options: { maxStep?: number } = {}): any[] {
  let previousReturner: FieldPlayer | null = null;
  const activeReturnerStates = new Set(['fielding', 'returning', 'tackled', 'being-tackled']);
  frames.forEach((frame) => {
    const returner = findPlayer(frame.defensePlayers, 'RETURNER');
    const returnerCarrying = frame.ball?.arcadeOwnerRole === 'RETURNER' || frame.ball?.arcadeState === 'carried';
    const returnerActive = returnerCarrying || activeReturnerStates.has(returner?.arcadeState);
    if (!returner) {
      previousReturner = null;
      return;
    }
    if (previousReturner && returnerActive) {
      const maxStep = options.maxStep ?? Math.max(7.5, playerPixelsPerFrame(returner.speedRating || 84, 'run', 1.25));
      const step = pointDistance(previousReturner, returner);
      if (step > maxStep) {
        const capped = movePointTowards(previousReturner, returner, maxStep);
        returner.x = capped.x;
        returner.y = capped.y;
        returner.moving = true;
        if (returnerCarrying) {
          frame.ball = {
            ...frame.ball,
            ...carriedBallPoint(returner, capped.x, capped.y, -direction),
          };
        }
      }
    }
    previousReturner = { ...returner };
  });
  return frames;
}

export function buildPuntLiveSequence(context: any): any[] {
  if (context.play?.arcadeReplay?.source === 'arcade-sim-v1' && context.play?.arcadePlaybackMode === 'raw') {
    return smoothPuntReturnerFrames(buildArcadePuntLiveSequence(context), context.direction, { maxStep: 5 });
  }
  const frameCount = liveFrameCount(context.play);
  const stopReason = stopReasonForPlay(context.play);
  const touchback = stopReason === 'touchback';
  const tackledReturn = stopReason === 'tackle' && !touchback;
  const puntReturnTd = stopReason === 'touchdown';
  const puntHangTime = puntHangTimeSeconds(context.play);
  const hangRatio = clampRange((puntHangTime - 3.85) / 1.3, 0, 1);
  const offenseBase = clonePlayers(context.formation.offense);
  const defenseBase = clonePlayers(context.formation.defense);
  const metrics = fieldMetrics();
  const xDelta = (value: number) => value * context.direction;
  const returnXDelta = (value: number) => value * -context.direction;
  const forwardReturnX = (baseX: number, targetX: number) =>
    (returnXDelta(1) < 0 ? Math.min(baseX, targetX) : Math.max(baseX, targetX));
  const coverageAdvanceX = (baseX: number, targetX: number) =>
    (xDelta(1) < 0 ? Math.min(baseX, targetX) : Math.max(baseX, targetX));
  const clampFieldY = (value: number) => clampRange(snapToPixel(value), metrics.fieldTop + 18, metrics.fieldBottom - 18);
  const sideForRole = (role: string) => (role.endsWith('1') ? -1 : 1);

  const landingYardLine = Math.max(30, Math.min(99, context.play.landingYardLine ?? context.play.endYardLine ?? 60));
  const landingX = screenXForYard(landingYardLine, context.direction);
  const catchPoint = { x: landingX, y: context.laneY };
  const returnSpot = { x: context.endX, y: context.laneY };
  const grossYards = Math.max(0, context.play.grossYards || Math.abs(landingYardLine - (context.play.startYardLine ?? landingYardLine)));
  const returnYards = Math.max(0, context.play.returnYards || Math.abs((context.play.endYardLine ?? landingYardLine) - landingYardLine));
  const shortReturnFinish = tackledReturn && returnYards <= 10;
  const mediumReturnFinish = tackledReturn && returnYards > 10 && returnYards <= 20;
  const coverageHangLift = shortReturnFinish
    ? 0.16 + clampRange((10 - returnYards) * 0.012, 0, 0.08)
    : mediumReturnFinish
      ? 0.06
      : 0;
  const coverageHangRatio = clampRange(hangRatio + coverageHangLift, 0, 1);
  const coverageCompression = clampRange(
    coverageHangRatio * 0.68
      + (1 - clampRange(returnYards / 16, 0, 1)) * 0.24
      + clampRange((42 - grossYards) / 18, -0.08, 0.12),
    0,
    1,
  );
  const visualHangBonus = 0.18
    + clampRange((12 - returnYards) * 0.008, 0, 0.1)
    + (shortReturnFinish ? 0.08 : mediumReturnFinish ? 0.03 : 0);
  const hangFrames = Math.max(124, Math.round((puntHangTime + visualHangBonus) * 30));
  const puntOperationFrames = clampRange(Math.round(puntOperationSeconds(context.play) * 30), 56, 63);
  const snapTravelFrames = clampRange(
    Math.round((1.08 - (playHash(context.play, 'punt-snap-travel') % 4) * 0.04) * 30),
    28,
    32,
  );
  const kickStrideFrames = Math.max(24, puntOperationFrames - snapTravelFrames);
  const puntReleaseFrame = snapTravelFrames + kickStrideFrames;
  const gunnerReleaseFrame = Math.max(1, Math.round(snapTravelFrames * 0.18));
  const rushReleaseFrame = Math.max(0, gunnerReleaseFrame - 1);
  const returnFramesFloor = touchback
    ? 14
    : puntReturnTd
      // TD returns use a direct path segment — budget 3.6 frames/yard so the returner
      // animates at ~10 yards/s with the frame count puntLiveDurationMs allocates.
      ? Math.round(returnYards * 3.6)
      : Math.max(
          34,
          Math.round(returnYards * (2.3 + (1 - coverageCompression) * 0.18))
            + (tackledReturn ? 16 : 12)
            - Math.round(coverageCompression * 5),
        );
  const totalFrames = Math.max(
    frameCount + (touchback ? 8 : tackledReturn ? 18 : 14),
    puntReleaseFrame + hangFrames + returnFramesFloor,
  );
  const cutDirection = Math.sign(context.play.visual?.lane || 0) || (playHash(context.play, 'punt-cut-side') % 2 === 0 ? -1 : 1);
  const cutWidth = clampRange(12 + returnYards * 0.78 + (playHash(context.play, 'punt-cut-width') % 5), 12, 26);
  const topForce = cutDirection < 0;
  const forceGunnerRole = topForce ? 'GUNNER1' : 'GUNNER2';
  const cleanupGunnerRole = topForce ? 'GUNNER2' : 'GUNNER1';
  const leadContainRole = topForce ? 'CONTAIN1' : 'CONTAIN2';
  const backsideContainRole = topForce ? 'CONTAIN2' : 'CONTAIN1';
  const supportProtectorEntries = ['PROTECT2', 'PROTECT3', 'PROTECT4', 'PROTECT5', 'PROTECT6']
    .map((role) => {
      const player = findPlayer(offenseBase, role);
      return player
        ? {
            role,
            player,
            distance: Math.abs(player.y - catchPoint.y),
            side: player.y <= context.centerY ? -1 : 1,
            kind: 'protector',
          }
        : null;
    })
    .filter(Boolean)
    .sort((left: any, right: any) => left.distance - right.distance)
    .slice(0, 4);
  const upbackPlayer = findPlayer(offenseBase, 'UPBACK');
  const upbackSupportEntry = upbackPlayer
    ? {
        role: 'UPBACK',
        player: upbackPlayer,
        distance: Math.abs(upbackPlayer.y - catchPoint.y),
        side: upbackPlayer.y <= context.centerY ? -1 : 1,
        kind: 'upback',
      }
    : null;
  const secondaryCoverageEntries = [...supportProtectorEntries, ...(upbackSupportEntry ? [upbackSupportEntry] : [])];
  const secondaryCoverageEntryByRole = new Map(secondaryCoverageEntries.map((entry: any) => [entry.role, entry]));
  const secondaryCoverageRoleSet = new Set(secondaryCoverageEntries.map((entry: any) => entry.role));
  const interiorBlockEntries = ['RUSH2', 'RUSH3', 'RUSH4', 'RUSH5', 'RUSH6']
    .map((role) => {
      const player = findPlayer(defenseBase, role);
      return player
        ? {
            role,
            player,
            distance: Math.abs(player.y - catchPoint.y),
            side: player.y <= context.centerY ? -1 : 1,
          }
        : null;
    })
    .filter(Boolean)
    .sort((left: any, right: any) => left.distance - right.distance)
    .slice(0, secondaryCoverageEntries.length);
  const blockerMatchByRole = new Map<string, string | null>();
  const coverageMatchByRole = new Map<string, string | null>();
  secondaryCoverageEntries.forEach((entry: any, index: number) => {
    const blocker = interiorBlockEntries[index] || null;
    coverageMatchByRole.set(entry.role, blocker?.role || null);
    if (blocker) {
      blockerMatchByRole.set(blocker.role, entry.role);
    }
  });
  const secondaryReleaseByRole = new Map<string, any>();
  const blockerSetupByRole = new Map<string, any>();
  secondaryCoverageEntries.forEach((entry: any, index: number) => {
    const blocker = interiorBlockEntries[index] || null;
    const sameSide = entry.side === cutDirection;
    const laneTargetY = clampFieldY(
      entry.kind === 'upback'
        ? context.laneY + cutDirection * 4
        : context.laneY + (sameSide ? cutDirection * (10 + index * 5) : entry.side * (8 + index * 6))
    );
    const engageBlend = entry.kind === 'upback'
      ? 0.18 + coverageHangRatio * 0.04
      : 0.2 + coverageHangRatio * 0.05 + index * 0.015;
    const engageBaseX = coverageAdvanceX(
      blocker?.player.x || entry.player.x,
      snapToPixel(lerp(
        blocker?.player.x || entry.player.x,
        catchPoint.x + xDelta(8 + index * 3),
        engageBlend,
      ))
    );
    secondaryReleaseByRole.set(entry.role, {
      x: coverageAdvanceX(entry.player.x, snapToPixel(engageBaseX - xDelta(4 + index))),
      y: clampFieldY(lerp(entry.player.y, laneTargetY + entry.side * 3, 0.28 + coverageHangRatio * 0.08)),
    });
    if (blocker) {
      blockerSetupByRole.set(blocker.role, {
        x: coverageAdvanceX(blocker.player.x, snapToPixel(engageBaseX + xDelta(6 + index))),
        y: clampFieldY(lerp(blocker.player.y, laneTargetY - entry.side * 3, 0.34 + coverageHangRatio * 0.1)),
      });
    }
  });

  const catchSettlePoint = {
    x: forwardReturnX(catchPoint.x, snapToPixel(lerp(catchPoint.x, returnSpot.x, 0.1) + returnXDelta(6))),
    y: clampFieldY(catchPoint.y + cutDirection * 4),
  };
  const pressPoint = {
    x: forwardReturnX(catchSettlePoint.x, snapToPixel(lerp(catchPoint.x, returnSpot.x, 0.44) + returnXDelta(8))),
    y: clampFieldY(lerp(catchSettlePoint.y, returnSpot.y, 0.08) + cutDirection * 8),
  };
  const cutPoint = {
    x: forwardReturnX(pressPoint.x, snapToPixel(lerp(catchPoint.x, returnSpot.x, 0.72))),
    y: clampFieldY(lerp(pressPoint.y, returnSpot.y, 0.16) + cutDirection * cutWidth),
  };
  const alleyPoint = {
    x: forwardReturnX(cutPoint.x, snapToPixel(lerp(catchPoint.x, returnSpot.x, 0.88))),
    y: clampFieldY(lerp(cutPoint.y, returnSpot.y, 0.42)),
  };
  const trackedEndRatio = shortReturnFinish ? 0.82 : mediumReturnFinish ? 0.9 : 0.97;
  const trackedReturnEnd = tackledReturn
    ? {
        x: forwardReturnX(alleyPoint.x, snapToPixel(lerp(catchPoint.x, returnSpot.x, trackedEndRatio))),
        y: clampFieldY(lerp(alleyPoint.y, returnSpot.y, shortReturnFinish ? 0.56 : mediumReturnFinish ? 0.66 : 0.74)),
      }
    : returnSpot;
  const returnGuide = compactPathPoints([catchPoint, catchSettlePoint, pressPoint, cutPoint, alleyPoint, trackedReturnEnd]);

  const snapperBase = findPlayer(offenseBase, 'PROTECT4') || findPlayer(offenseBase, 'PROTECT3') || offenseBase[0];
  const punterBase = findPlayer(offenseBase, 'PUNTER') || offenseBase[0];
  const puntBallPointAt = (point: { x: number; y: number }, fallbackX: number, fallbackY: number) => (
    puntCarryBallPoint({ ...punterBase, x: point.x, y: point.y }, context.direction, fallbackX, fallbackY)
  );
  const snapOrigin = puntSnapBallPoint(snapperBase, context.direction, context.losX, snapperBase.y + 8);
  const punterReceivePoint = { x: snapToPixel(punterBase.x + xDelta(1)), y: snapToPixel(punterBase.y) };
  const punterPlantPoint = { x: snapToPixel(punterBase.x + xDelta(5)), y: snapToPixel(punterBase.y + 2) };
  const puntKickPoint = { x: snapToPixel(punterBase.x + xDelta(10)), y: snapToPixel(punterBase.y + 4) };
  const snapTarget = puntBallPointAt(punterReceivePoint, punterReceivePoint.x + 6, punterReceivePoint.y + 8);
  const puntOrigin = puntBallPointAt(puntKickPoint, puntKickPoint.x + 6, puntKickPoint.y + 8);

  const lastFrame = totalFrames - 1;
  const flightEnd = Math.min(lastFrame - (touchback ? 10 : 26), puntReleaseFrame + hangFrames);
  const catchSecureDelay = touchback ? 0 : Math.max(2, Math.round(5 - hangRatio * 1.5));
  const trackedReturnFrames = touchback
    ? 6
    : puntReturnTd
      ? Math.round(returnYards * 3.6) + 8
      : returnFramesFloor + (tackledReturn ? Math.max(6, 12 - Math.round(coverageCompression * 5)) : 8);
  const trackedEnd = touchback
    ? Math.min(lastFrame, flightEnd + 6)
    : Math.min(lastFrame, flightEnd + trackedReturnFrames);
  const lastTrackedFrame = trackedEnd;

  const offenseTracks = new Map();
  const defenseTracks = new Map();
  const addPath = (segments: any[], startFrame: number, endFrame: number, points: any[], sample = 'polyline', moving = true) => {
    if (endFrame <= startFrame) {
      return;
    }
    segments.push(createPathSegment(startFrame, endFrame, points, sample, moving));
  };
  const addHold = (segments: any[], startFrame: number, endFrame: number, point: any, moving = false) => {
    if (endFrame < startFrame) {
      return;
    }
    segments.push(createHoldSegment(startFrame, endFrame, point, moving));
  };
  const trackPlayer = (tracks: Map<string, any>, role: string, basePoint: any, builder: (basePoint: any) => any[]) => {
    tracks.set(role, builder(basePoint).filter(Boolean));
  };

  trackPlayer(defenseTracks, 'RETURNER', findPlayer(defenseBase, 'RETURNER') || defenseBase[0], (basePoint) => {
    const segments: any[] = [];
    addHold(segments, 0, puntReleaseFrame, basePoint, false);
    addPath(segments, puntReleaseFrame, flightEnd, [basePoint, catchPoint], 'spline', true);
    if (touchback) {
      addHold(segments, flightEnd, lastTrackedFrame, catchPoint, false);
    } else if (puntReturnTd) {
      // For a TD return the physics system can't cover the full distance in the allotted
      // frames.  Use a direct path segment instead so the returner reliably reaches the
      // end zone.  Coverage players remain physics-driven (they target returnSpot since
      // returnerState is absent from dynamicStates).
      addPath(segments, flightEnd, lastTrackedFrame, returnGuide, 'spline', true);
    }
    return segments;
  });

  const shieldSetPoint = {
    x: coverageAdvanceX(
      findPlayer(defenseBase, 'SHIELD')?.x || catchPoint.x,
      snapToPixel(lerp(
        (findPlayer(defenseBase, 'SHIELD')?.x || catchPoint.x),
        orientX(landingX, -26, context.direction),
        0.56 + hangRatio * 0.22,
      ))
    ),
    y: clampFieldY(lerp(
      findPlayer(defenseBase, 'SHIELD')?.y || context.centerY,
      context.laneY - cutDirection * 6,
      0.56 + hangRatio * 0.18,
    )),
  };
  const shieldGuide = compactPathPoints([
    shieldSetPoint,
    {
      x: forwardReturnX(shieldSetPoint.x, snapToPixel(pressPoint.x + returnXDelta(18))),
      y: clampFieldY(pressPoint.y - cutDirection * 10),
    },
    {
      x: forwardReturnX(shieldSetPoint.x, snapToPixel(alleyPoint.x + returnXDelta(14))),
      y: clampFieldY(alleyPoint.y - cutDirection * 12),
    },
    {
      x: forwardReturnX(shieldSetPoint.x, snapToPixel(trackedReturnEnd.x + returnXDelta(10))),
      y: clampFieldY(trackedReturnEnd.y - cutDirection * 10),
    },
  ]);

  trackPlayer(defenseTracks, 'SHIELD', findPlayer(defenseBase, 'SHIELD') || defenseBase[0], (basePoint) => {
    const segments: any[] = [];
    addHold(segments, 0, puntReleaseFrame, basePoint, false);
    addPath(segments, puntReleaseFrame, flightEnd, [basePoint, shieldSetPoint], 'spline', true);
    if (touchback) {
      addHold(segments, flightEnd, lastTrackedFrame, shieldSetPoint, false);
    }
    return segments;
  });

  ['CONTAIN1', 'CONTAIN2'].forEach((role) => {
    const side = sideForRole(role);
    const basePlayer = findPlayer(defenseBase, role) || defenseBase[0];
    const vicePoint = {
      x: coverageAdvanceX(
        basePlayer.x,
        snapToPixel(lerp(basePlayer.x, orientX(landingX, -18, context.direction), 0.5 + hangRatio * 0.2))
      ),
      y: clampFieldY(lerp(basePlayer.y, catchPoint.y + side * (78 - hangRatio * 12), 0.22 + hangRatio * 0.14)),
    };
    trackPlayer(defenseTracks, role, basePlayer, (basePoint) => {
      const segments: any[] = [];
      addHold(segments, 0, puntReleaseFrame, basePoint, false);
      addPath(segments, puntReleaseFrame, flightEnd, [basePoint, vicePoint], 'spline', true);
      if (touchback) {
        addHold(segments, flightEnd, lastTrackedFrame, vicePoint, false);
      }
      return segments;
    });
  });

  for (let index = 1; index <= 7; index += 1) {
    const role = 'RUSH' + index;
    trackPlayer(defenseTracks, role, findPlayer(defenseBase, role) || defenseBase[0], (basePoint) => {
      const segments: any[] = [];
      const blockerSetup = blockerSetupByRole.get(role);
      if (blockerSetup && !touchback) {
        const jabPoint = {
          x: snapToPixel(basePoint.x + xDelta(-6)),
          y: clampFieldY(lerp(basePoint.y, blockerSetup.y, 0.08)),
        };
        const climbPoint = {
          x: coverageAdvanceX(basePoint.x, snapToPixel(lerp(basePoint.x, blockerSetup.x, 0.56))),
          y: clampFieldY(lerp(basePoint.y, blockerSetup.y, 0.42)),
        };
        addPath(segments, 0, rushReleaseFrame + 2, [basePoint, jabPoint], 'polyline', true);
        addPath(segments, rushReleaseFrame + 2, flightEnd, [jabPoint, climbPoint, blockerSetup], 'spline', true);
        addHold(segments, flightEnd, lastTrackedFrame, blockerSetup, false);
        return segments;
      }
      const rushPoint = { x: snapToPixel(basePoint.x + xDelta(-10)), y: basePoint.y };
      addPath(segments, 0, rushReleaseFrame + 3, [basePoint, rushPoint], 'polyline', true);
      addHold(segments, rushReleaseFrame + 3, lastTrackedFrame, rushPoint, false);
      return segments;
    });
  }

  ['GUNNER1', 'GUNNER2'].forEach((role) => {
    const side = sideForRole(role);
    const basePlayer = findPlayer(offenseBase, role) || offenseBase[0];
    const gunnerCatchOffsetX = shortReturnFinish ? -40 : mediumReturnFinish ? -24 : 20;
    const gunnerPivotBlend = shortReturnFinish
      ? 0.8 + coverageHangRatio * 0.08
      : mediumReturnFinish
        ? 0.68 + coverageHangRatio * 0.08 + coverageCompression * 0.04
        : 0.5 + coverageHangRatio * 0.18 + coverageCompression * 0.14;
    const pivotPoint = {
      x: coverageAdvanceX(
        basePlayer.x,
        snapToPixel(lerp(
          basePlayer.x,
          catchPoint.x + xDelta(gunnerCatchOffsetX),
          gunnerPivotBlend,
        ))
      ),
      y: clampFieldY(lerp(
        basePlayer.y,
        context.centerY + side * (shortReturnFinish ? 62 : mediumReturnFinish ? 78 : (88 - coverageHangRatio * 10)),
        shortReturnFinish ? 0.34 + coverageHangRatio * 0.12 : 0.24 + coverageHangRatio * 0.12,
      )),
    };
    trackPlayer(offenseTracks, role, basePlayer, (basePoint) => {
      const segments: any[] = [];
      addHold(segments, 0, gunnerReleaseFrame, basePoint, false);
      addPath(segments, gunnerReleaseFrame, flightEnd, [basePoint, pivotPoint], 'spline', true);
      if (touchback) {
        addHold(segments, flightEnd, lastTrackedFrame, pivotPoint, false);
      }
      return segments;
    });
  });

  for (let index = 1; index <= 7; index += 1) {
    const role = 'PROTECT' + index;
    trackPlayer(offenseTracks, role, findPlayer(offenseBase, role) || offenseBase[0], (basePoint) => {
      const segments: any[] = [];
      const releasePoint = secondaryReleaseByRole.get(role);
      const driftPoint = releasePoint
        ? {
            x: coverageAdvanceX(basePoint.x, snapToPixel(lerp(basePoint.x, releasePoint.x, 0.36))),
            y: clampFieldY(lerp(basePoint.y, releasePoint.y, 0.18)),
          }
        : {
            x: coverageAdvanceX(basePoint.x, snapToPixel(basePoint.x + xDelta(10))),
            y: basePoint.y,
          };
      addHold(segments, 0, rushReleaseFrame, basePoint, false);
      if (releasePoint && !touchback) {
        addPath(segments, rushReleaseFrame, flightEnd, [basePoint, driftPoint, releasePoint], 'spline', true);
        addHold(segments, flightEnd, lastTrackedFrame, releasePoint, false);
        return segments;
      }
      addPath(segments, rushReleaseFrame, Math.round(flightEnd * 0.55), [basePoint, driftPoint], 'polyline', true);
      addHold(segments, Math.round(flightEnd * 0.55), lastTrackedFrame, driftPoint, false);
      return segments;
    });
  }

  trackPlayer(offenseTracks, 'PUNTER', punterBase, (basePoint) => {
    const segments: any[] = [];
    addPath(segments, 0, snapTravelFrames, [basePoint, punterReceivePoint], 'polyline', true);
    addPath(segments, snapTravelFrames, puntReleaseFrame, [punterReceivePoint, punterPlantPoint, puntKickPoint], 'spline', true);
    addHold(segments, puntReleaseFrame, lastTrackedFrame, puntKickPoint, false);
    return segments;
  });

  trackPlayer(offenseTracks, 'UPBACK', findPlayer(offenseBase, 'UPBACK') || offenseBase[0], (basePoint) => {
    const segments: any[] = [];
    const releasePoint = secondaryReleaseByRole.get('UPBACK');
    const shiftPoint = releasePoint
      ? {
          x: coverageAdvanceX(basePoint.x, snapToPixel(lerp(basePoint.x, releasePoint.x, 0.28))),
          y: clampFieldY(lerp(basePoint.y, releasePoint.y, 0.22)),
        }
      : { x: basePoint.x, y: snapToPixel(lerp(basePoint.y, context.centerY, 0.18)) };
    addHold(segments, 0, rushReleaseFrame, basePoint, false);
    if (releasePoint && !touchback) {
      addPath(segments, rushReleaseFrame, flightEnd, [basePoint, shiftPoint, releasePoint], 'spline', true);
      addHold(segments, flightEnd, lastTrackedFrame, releasePoint, false);
      return segments;
    }
    addPath(segments, rushReleaseFrame, Math.round(flightEnd * 0.4), [basePoint, shiftPoint], 'polyline', true);
    addHold(segments, Math.round(flightEnd * 0.4), lastTrackedFrame, shiftPoint, false);
    return segments;
  });

  const leadContainBase = findPlayer(defenseBase, leadContainRole) || defenseBase[0];
  const backsideContainBase = findPlayer(defenseBase, backsideContainRole) || defenseBase[0];
  const leadContainSetup = evaluateTrack(defenseTracks.get(leadContainRole), flightEnd, leadContainBase);
  const backsideContainSetup = evaluateTrack(defenseTracks.get(backsideContainRole), flightEnd, backsideContainBase);
  const forceGunnerBase = findPlayer(offenseBase, forceGunnerRole) || offenseBase[0];
  const cleanupGunnerBase = findPlayer(offenseBase, cleanupGunnerRole) || offenseBase[0];
  const forceGunnerSetup = evaluateTrack(offenseTracks.get(forceGunnerRole), flightEnd, forceGunnerBase);
  const cleanupGunnerSetup = evaluateTrack(offenseTracks.get(cleanupGunnerRole), flightEnd, cleanupGunnerBase);
  const secondaryCoveragePlans = new Map<string, any>();
  secondaryCoverageEntries.forEach((entry: any, index: number) => {
    const setup = evaluateTrack(offenseTracks.get(entry.role), flightEnd, entry.player);
    const sameSide = entry.side === cutDirection;
    const pressOffsetX = entry.kind === 'upback'
      ? 40
      : sameSide ? 34 + index * 4 : 42 + index * 4;
    const pressOffsetY = entry.kind === 'upback'
      ? cutDirection * 10
      : sameSide ? cutDirection * (22 + index * 6) : -cutDirection * (16 + index * 6);
    const alleyOffsetX = entry.kind === 'upback'
      ? 26
      : sameSide ? 24 + index * 4 : 30 + index * 4;
    const alleyOffsetY = entry.kind === 'upback'
      ? cutDirection * 8
      : sameSide ? cutDirection * (12 + index * 5) : -cutDirection * (8 + index * 5);
    const finishOffsetX = entry.kind === 'upback'
      ? 20
      : 18 + index * 4;
    const finishOffsetY = entry.kind === 'upback'
      ? cutDirection * 6
      : sameSide ? cutDirection * (8 + index * 4) : -cutDirection * (6 + index * 4);
    secondaryCoveragePlans.set(entry.role, {
      team: 'offense',
      family: 'pass',
      kind: 'secondary-coverage',
      scale: entry.kind === 'upback'
        ? 0.66
        : sameSide
          ? 0.74 - index * 0.02
          : 0.64 - index * 0.02,
      sample: 'spline',
      delay: Math.max(0, catchSecureDelay + (entry.kind === 'upback' ? 3 : sameSide ? index : 2 + index) - Math.round(coverageCompression * 2)),
      side: entry.side,
      targetOffsetX: finishOffsetX,
      targetOffsetY: finishOffsetY,
      trackBlend: entry.kind === 'upback' ? 0.12 : sameSide ? 0.16 : 0.12,
      closeBlend: entry.kind === 'upback' ? 0.2 : sameSide ? 0.26 : 0.2,
      finishDelay: Math.max(0, (entry.kind === 'upback' ? 1 : sameSide ? index : index + 2) - Math.round(coverageCompression * 2)),
      finishScale: entry.kind === 'upback'
        ? 0.6
        : sameSide
          ? 0.66 - index * 0.02
          : 0.54 - index * 0.02,
      guide: compactPathPoints([
        setup,
        {
          x: coverageAdvanceX(setup.x, snapToPixel(pressPoint.x + xDelta(pressOffsetX))),
          y: clampFieldY(pressPoint.y + pressOffsetY),
        },
        {
          x: coverageAdvanceX(setup.x, snapToPixel(alleyPoint.x + xDelta(alleyOffsetX))),
          y: clampFieldY(alleyPoint.y + alleyOffsetY),
        },
        {
          x: coverageAdvanceX(
            setup.x,
            snapToPixel(trackedReturnEnd.x + xDelta(Math.max(8, finishOffsetX - Math.round(coverageCompression * 6)))),
          ),
          y: clampFieldY(trackedReturnEnd.y + finishOffsetY),
        },
      ]),
    });
  });
  const escortBlockPlans = new Map<string, any>();
  interiorBlockEntries.forEach((entry: any, index: number) => {
    const setup = evaluateTrack(defenseTracks.get(entry.role), flightEnd, entry.player);
    const matchedCoverageRole = blockerMatchByRole.get(entry.role) || null;
    const matchedCoverageEntry = secondaryCoverageEntries.find((coverageEntry: any) => coverageEntry.role === matchedCoverageRole) || null;
    const matchedSide = matchedCoverageEntry?.side ?? entry.side;
    escortBlockPlans.set(entry.role, {
      team: 'defense',
      family: 'run',
      kind: 'escort-block',
      scale: 0.6 - index * 0.02,
      sample: 'spline',
      delay: catchSecureDelay + 1 + index,
      matchRole: matchedCoverageRole,
      screenOffsetX: 16 + index * 3,
      screenOffsetY: matchedSide * (12 + index * 4),
      finishDelay: index,
      finishScale: 0.38 + Math.max(0, 0.08 - index * 0.01),
      guide: compactPathPoints([
        setup,
        {
          x: forwardReturnX(setup.x, snapToPixel(pressPoint.x + returnXDelta(22 + index * 4))),
          y: clampFieldY(pressPoint.y + matchedSide * (16 + index * 5)),
        },
        {
          x: forwardReturnX(setup.x, snapToPixel(alleyPoint.x + returnXDelta(16 + index * 4))),
          y: clampFieldY(alleyPoint.y + matchedSide * (12 + index * 4)),
        },
        {
          x: forwardReturnX(setup.x, snapToPixel(trackedReturnEnd.x + returnXDelta(10 + index * 3))),
          y: clampFieldY(trackedReturnEnd.y + matchedSide * (8 + index * 4)),
        },
      ]),
    });
  });
  const frontsideCoverageEntries = secondaryCoverageEntries.filter((entry: any) => entry.side === cutDirection);
  const trailCoverageEntries = secondaryCoverageEntries.filter((entry: any) => entry.side !== cutDirection);
  const frontsideFinishEntry = frontsideCoverageEntries.length
    ? frontsideCoverageEntries[playHash(context.play, 'punt-frontside-finish') % frontsideCoverageEntries.length]
    : secondaryCoverageEntries[0] || null;
  const trailFinishEntry = trailCoverageEntries.length
    ? trailCoverageEntries[playHash(context.play, 'punt-trail-finish') % trailCoverageEntries.length]
    : secondaryCoverageEntries.find((entry: any) => entry.role !== frontsideFinishEntry?.role) || null;
  const finishRoll = playHash(context.play, 'punt-finish-owner') % 100;
  const baseFinishRoles = (() => {
    if (shortReturnFinish) {
      return [forceGunnerRole, cleanupGunnerRole, frontsideFinishEntry?.role || trailFinishEntry?.role || cleanupGunnerRole];
    }
    if (mediumReturnFinish && finishRoll < 44) {
      return [forceGunnerRole, frontsideFinishEntry?.role || cleanupGunnerRole, cleanupGunnerRole];
    }
    if (frontsideFinishEntry && coverageCompression >= 0.68 && finishRoll < 54) {
      return [frontsideFinishEntry.role, forceGunnerRole, cleanupGunnerRole];
    }
    if (finishRoll < 18) {
      return [cleanupGunnerRole, frontsideFinishEntry?.role || forceGunnerRole, forceGunnerRole];
    }
    if (finishRoll < 70) {
      return [forceGunnerRole, frontsideFinishEntry?.role || cleanupGunnerRole, cleanupGunnerRole];
    }
    if (trailFinishEntry) {
      return [trailFinishEntry.role, forceGunnerRole, frontsideFinishEntry?.role || cleanupGunnerRole];
    }
    return [forceGunnerRole, frontsideFinishEntry?.role || cleanupGunnerRole, cleanupGunnerRole];
  })();
  const finishRoles = baseFinishRoles.filter((role: string | null, index: number, roles: (string | null)[]) =>
    !!role && roles.indexOf(role) === index,
  ) as string[];
  const primaryFinishRole = finishRoles[0] || forceGunnerRole;
  const finishRankForRole = (role: string) => {
    const finishIndex = finishRoles.indexOf(role);
    return finishIndex >= 0 ? finishIndex : 99;
  };
  const finishSideForRole = (role: string) => secondaryCoverageEntryByRole.get(role)?.side || sideForRole(role);
  const primaryFinishIsInterior = secondaryCoverageRoleSet.has(primaryFinishRole);

  const leadContainGuide = compactPathPoints([
    leadContainSetup,
    {
      x: forwardReturnX(leadContainSetup.x, snapToPixel(cutPoint.x + returnXDelta(22))),
      y: clampFieldY(cutPoint.y + cutDirection * 14),
    },
    {
      x: forwardReturnX(leadContainSetup.x, snapToPixel(alleyPoint.x + returnXDelta(16))),
      y: clampFieldY(alleyPoint.y + cutDirection * 12),
    },
    {
      x: forwardReturnX(leadContainSetup.x, snapToPixel(trackedReturnEnd.x + returnXDelta(10))),
      y: clampFieldY(trackedReturnEnd.y + cutDirection * 10),
    },
  ]);
  const backsideContainGuide = compactPathPoints([
    backsideContainSetup,
    {
      x: forwardReturnX(backsideContainSetup.x, snapToPixel(pressPoint.x + returnXDelta(12))),
      y: clampFieldY(pressPoint.y - cutDirection * 22),
    },
    {
      x: forwardReturnX(backsideContainSetup.x, snapToPixel(alleyPoint.x + returnXDelta(10))),
      y: clampFieldY(alleyPoint.y - cutDirection * 20),
    },
    {
      x: forwardReturnX(backsideContainSetup.x, snapToPixel(trackedReturnEnd.x + returnXDelta(6))),
      y: clampFieldY(trackedReturnEnd.y - cutDirection * 18),
    },
  ]);
  const forceGunnerGuide = compactPathPoints([
    forceGunnerSetup,
    {
      x: coverageAdvanceX(forceGunnerSetup.x, snapToPixel(pressPoint.x + xDelta(shortReturnFinish ? 36 : 48))),
      y: clampFieldY(pressPoint.y + cutDirection * (shortReturnFinish ? 34 : 46)),
    },
    {
      x: coverageAdvanceX(forceGunnerSetup.x, snapToPixel(alleyPoint.x + xDelta(shortReturnFinish ? 28 : 36))),
      y: clampFieldY(alleyPoint.y + cutDirection * (shortReturnFinish ? 20 : 28)),
    },
    {
      x: coverageAdvanceX(
        forceGunnerSetup.x,
        snapToPixel(trackedReturnEnd.x + xDelta(shortReturnFinish
          ? Math.max(10, 20 - Math.round(coverageCompression * 8))
          : Math.max(14, 28 - Math.round(coverageCompression * 10)))),
      ),
      y: clampFieldY(trackedReturnEnd.y + cutDirection * (shortReturnFinish
        ? Math.max(6, 12 - Math.round(coverageCompression * 4))
        : Math.max(10, 20 - Math.round(coverageCompression * 6)))),
    },
  ]);
  const cleanupGunnerGuide = compactPathPoints([
    cleanupGunnerSetup,
    {
      x: coverageAdvanceX(cleanupGunnerSetup.x, snapToPixel(cutPoint.x + xDelta(42))),
      y: clampFieldY(cutPoint.y - cutDirection * 34),
    },
    {
      x: coverageAdvanceX(cleanupGunnerSetup.x, snapToPixel(alleyPoint.x + xDelta(38))),
      y: clampFieldY(alleyPoint.y - cutDirection * 24),
    },
    {
      x: coverageAdvanceX(
        cleanupGunnerSetup.x,
        snapToPixel(trackedReturnEnd.x + xDelta(Math.max(18, 32 - Math.round(coverageCompression * 8)))),
      ),
      y: clampFieldY(trackedReturnEnd.y - cutDirection * Math.max(10, 18 - Math.round(coverageCompression * 4))),
    },
  ]);

  const dynamicPlans = new Map<string, any>([
    ['RETURNER', { team: 'defense', family: 'run', scale: 0.92, sample: 'spline', delay: Math.max(1, catchSecureDelay - 1), guide: returnGuide }],
    ['SHIELD', { team: 'defense', family: 'run', scale: 0.66, sample: 'spline', delay: catchSecureDelay + 1, guide: shieldGuide }],
    [leadContainRole, { team: 'defense', family: 'run', scale: 0.68, sample: 'spline', delay: catchSecureDelay + 2, guide: leadContainGuide }],
    [backsideContainRole, { team: 'defense', family: 'run', scale: 0.64, sample: 'spline', delay: catchSecureDelay + 4, guide: backsideContainGuide }],
    [forceGunnerRole, { team: 'offense', family: 'pass', scale: (shortReturnFinish ? 0.82 : 0.72) - coverageHangRatio * 0.03, sample: 'spline', delay: Math.max(0, catchSecureDelay + (shortReturnFinish ? 1 : 2) - Math.round(coverageCompression)), guide: forceGunnerGuide }],
    [cleanupGunnerRole, { team: 'offense', family: 'pass', scale: 0.64 - coverageHangRatio * 0.03, sample: 'spline', delay: Math.max(0, catchSecureDelay + 5 - Math.round(coverageCompression)), guide: cleanupGunnerGuide }],
  ]);
  secondaryCoveragePlans.forEach((plan, role) => dynamicPlans.set(role, plan));
  escortBlockPlans.forEach((plan, role) => dynamicPlans.set(role, plan));
  // For a TD return the RETURNER is driven by its track segment, not physics.
  if (puntReturnTd) dynamicPlans.delete('RETURNER');
  const escortBlockRoleSet = new Set(escortBlockPlans.keys());
  const dynamicStates = new Map<string, any>();
  if (!touchback) {
    dynamicPlans.forEach((plan, role) => {
      const basePlayers = plan.team === 'offense' ? offenseBase : defenseBase;
      const tracks = plan.team === 'offense' ? offenseTracks : defenseTracks;
      const state = seedMotionState(role, basePlayers, tracks, flightEnd);
      if (state) {
        if (role === 'RETURNER') {
          state.vx *= 0.22;
          state.vy *= 0.22;
        } else if (role === 'SHIELD') {
          state.vx *= 0.4;
          state.vy *= 0.4;
        } else if (role.startsWith('CONTAIN')) {
          state.vx *= 0.48;
          state.vy *= 0.48;
        } else if (role.startsWith('GUNNER')) {
          state.vx *= 0.32;
          state.vy *= 0.32;
        } else if (secondaryCoverageRoleSet.has(role)) {
          state.vx *= 0.26;
          state.vy *= 0.26;
        } else if (escortBlockRoleSet.has(role)) {
          state.vx *= 0.22;
          state.vy *= 0.22;
        }
        dynamicStates.set(role, state);
      }
    });
  }

  const applyContest = (
    coverageRole: string,
    blockerRole: string | null | undefined,
    returnProgress: number,
    options: {
      side: number;
      engageDistance: number;
      breakthroughProgress: number;
      holdStrength: number;
      coverageDrag: number;
      blockerDrag: number;
      coverageOffsetX: number;
      coverageOffsetY: number;
      blockerOffsetX: number;
      blockerOffsetY: number;
    },
  ) => {
    if (!blockerRole) {
      return;
    }

    const coverageState = dynamicStates.get(coverageRole);
    const blockerState = dynamicStates.get(blockerRole);
    if (!coverageState || !blockerState) {
      return;
    }

    const gap = pointDistance(coverageState, blockerState);
    if (gap > options.engageDistance) {
      return;
    }

    const progressWindow = clamp01(1 - returnProgress / Math.max(0.08, options.breakthroughProgress));
    const contactBlend = clamp01(1 - gap / Math.max(1, options.engageDistance));
    const contestStrength = progressWindow * options.holdStrength * (0.42 + contactBlend * 0.58);
    if (contestStrength <= 0.02) {
      return;
    }

    const midpoint = {
      x: snapToPixel(lerp(coverageState.x, blockerState.x, 0.5)),
      y: snapToPixel(lerp(coverageState.y, blockerState.y, 0.5)),
    };
    const coverageTarget = {
      x: snapToPixel(midpoint.x + xDelta(options.coverageOffsetX)),
      y: clampFieldY(midpoint.y + options.side * options.coverageOffsetY),
    };
    const blockerTarget = {
      x: snapToPixel(midpoint.x + returnXDelta(options.blockerOffsetX)),
      y: clampFieldY(midpoint.y + options.side * options.blockerOffsetY),
    };

    coverageState.x = snapToPixel(lerp(coverageState.x, coverageTarget.x, contestStrength * 0.36));
    coverageState.y = snapToPixel(lerp(coverageState.y, coverageTarget.y, contestStrength * 0.36));
    blockerState.x = snapToPixel(lerp(blockerState.x, blockerTarget.x, contestStrength * 0.34));
    blockerState.y = snapToPixel(lerp(blockerState.y, blockerTarget.y, contestStrength * 0.34));

    const coverageSlow = Math.max(0.54, 1 - contestStrength * options.coverageDrag);
    const blockerSlow = Math.max(0.72, 1 - contestStrength * options.blockerDrag);
    coverageState.vx *= coverageSlow;
    coverageState.vy *= coverageSlow;
    blockerState.vx *= blockerSlow;
    blockerState.vy *= blockerSlow;

    const sharedVX = lerp(coverageState.vx, blockerState.vx, 0.5);
    const sharedVY = lerp(coverageState.vy, blockerState.vy, 0.5);
    coverageState.vx = lerp(coverageState.vx, sharedVX, contestStrength * 0.22);
    coverageState.vy = lerp(coverageState.vy, sharedVY, contestStrength * 0.22);
    blockerState.vx = lerp(blockerState.vx, sharedVX, contestStrength * 0.34);
    blockerState.vy = lerp(blockerState.vy, sharedVY, contestStrength * 0.34);
  };

  const frames: any[] = [];
  for (let frame = 0; frame <= lastTrackedFrame; frame += 1) {
    const offensePlayers = offenseBase.map((player: any) => {
      const next = evaluateTrack(offenseTracks.get(player.role), frame, player);
      return { ...player, x: next.x, y: next.y, moving: next.moving };
    });
    const defensePlayers = defenseBase.map((player: any) => {
      const next = evaluateTrack(defenseTracks.get(player.role), frame, player);
      return { ...player, x: next.x, y: next.y, moving: next.moving };
    });

    if (!touchback && frame > flightEnd) {
      const returnerState = dynamicStates.get('RETURNER');
      const returnProgress = clamp01((frame - flightEnd) / Math.max(1, lastTrackedFrame - flightEnd));
      const dynamicOrder = [
        'RETURNER',
        'SHIELD',
        leadContainRole,
        backsideContainRole,
        ...interiorBlockEntries.map((entry: any) => entry.role),
        forceGunnerRole,
        cleanupGunnerRole,
        ...secondaryCoverageEntries.map((entry: any) => entry.role),
      ];

      dynamicOrder.forEach((role) => {
        const plan = dynamicPlans.get(role);
        const state = dynamicStates.get(role);
        if (!plan || !state) {
          return;
        }

        const progress = clamp01(
          (frame - flightEnd - (plan.delay || 0)) / Math.max(1, lastTrackedFrame - flightEnd - (plan.delay || 0))
        );
        const guideDriftSign = role === 'RETURNER'
          ? cutDirection
          : role === 'SHIELD'
            ? -cutDirection
            : role === leadContainRole
              ? cutDirection
              : role === backsideContainRole
                ? -cutDirection
                : Math.sign(plan.screenOffsetY || plan.targetOffsetY || 0);
        let desired = flowingGuideTarget(plan.guide, progress, plan.sample, {
          lookahead: role === 'RETURNER' ? 0.086 : role.startsWith('GUNNER') ? 0.07 : 0.058,
          settleBlend: role === 'RETURNER' ? 0.68 : 0.58,
          driftSign: guideDriftSign,
          driftScale: role === 'RETURNER' ? 0.84 : role.startsWith('GUNNER') ? 0.34 : escortBlockRoleSet.has(role) ? 0.3 : 0.24,
          minDrift: role === 'RETURNER' ? 0.7 : 0.2,
          maxDrift: role === 'RETURNER' ? 4.2 : role.startsWith('GUNNER') ? 2.4 : 1.9,
          nearDriftDistance: 8,
          farDriftDistance: role === 'RETURNER' ? 36 : 24,
        });
        const projectedCarrier = returnerState ? projectedPoint(returnerState, role.startsWith('GUNNER') ? 4 : 3) : trackedReturnEnd;

        if (role === 'SHIELD') {
          desired = blendPoints(desired, {
            x: snapToPixel(projectedCarrier.x + returnXDelta(16)),
            y: clampFieldY(projectedCarrier.y - cutDirection * 8),
          }, 0.16);
        } else if (role === leadContainRole) {
          desired = blendPoints(desired, {
            x: snapToPixel(projectedCarrier.x + returnXDelta(22)),
            y: clampFieldY(projectedCarrier.y + cutDirection * 16),
          }, 0.15);
        } else if (role === backsideContainRole) {
          desired = blendPoints(desired, {
            x: snapToPixel(projectedCarrier.x + returnXDelta(10)),
            y: clampFieldY(projectedCarrier.y - cutDirection * 20),
          }, 0.12);
        } else if (role === forceGunnerRole) {
          const finishRank = finishRankForRole(role);
          desired = blendPoints(desired, {
            x: snapToPixel(projectedCarrier.x + xDelta(shortReturnFinish
              ? (finishRank === 0 ? 18 : finishRank === 1 ? 28 : 36)
              : (finishRank === 0 ? 28 : finishRank === 1 ? 34 : 40))),
            y: clampFieldY(projectedCarrier.y + cutDirection * (shortReturnFinish
              ? (finishRank === 0 ? 10 : finishRank === 1 ? 18 : 24)
              : (finishRank === 0 ? 20 : finishRank === 1 ? 24 : 28))),
          }, progress > (shortReturnFinish ? 0.72 : 0.82)
            ? (shortReturnFinish
              ? (finishRank === 0 ? 0.42 : 0.22)
              : (finishRank === 0 ? 0.24 : 0.16))
            : (shortReturnFinish
              ? (finishRank === 0 ? 0.22 : 0.12)
              : (finishRank === 0 ? 0.12 : 0.08)));
        } else if (role === cleanupGunnerRole) {
          const finishRank = finishRankForRole(role);
          desired = blendPoints(desired, {
            x: snapToPixel(projectedCarrier.x + xDelta(finishRank === 0 ? 30 : finishRank === 1 ? 38 : 46)),
            y: clampFieldY(projectedCarrier.y - cutDirection * (finishRank === 0 ? 14 : finishRank === 1 ? 16 : 18)),
          }, progress > 0.84 ? (finishRank <= 1 ? 0.16 : 0.12) : (finishRank === 0 ? 0.1 : 0.06));
        } else if (secondaryCoverageRoleSet.has(role)) {
          const finishRank = finishRankForRole(role);
          desired = blendPoints(desired, {
            x: snapToPixel(projectedCarrier.x + xDelta(
              Math.max(6, (plan.targetOffsetX || 20) - (finishRank === 0 ? 10 : finishRank === 1 ? 4 : 0))
            )),
            y: clampFieldY(projectedCarrier.y + (plan.targetOffsetY || 0) - finishSideForRole(role) * (finishRank === 0 ? 4 : 0)),
          }, progress > 0.8
            ? (finishRank === 0 ? Math.max(plan.closeBlend || 0.14, 0.26) : plan.closeBlend || 0.14)
            : (finishRank === 0 ? Math.max(plan.trackBlend || 0.08, 0.14) : plan.trackBlend || 0.08));
        } else if (escortBlockRoleSet.has(role)) {
          const matchedState = plan.matchRole ? dynamicStates.get(plan.matchRole) : null;
          const screenTarget = matchedState
            ? {
                x: snapToPixel(lerp(
                  projectedCarrier.x + returnXDelta(plan.screenOffsetX || 18),
                  matchedState.x + returnXDelta(4),
                  0.42,
                )),
                y: clampFieldY(lerp(projectedCarrier.y + (plan.screenOffsetY || 0), matchedState.y, 0.58)),
              }
            : {
                x: snapToPixel(projectedCarrier.x + returnXDelta(plan.screenOffsetX || 18)),
                y: clampFieldY(projectedCarrier.y + (plan.screenOffsetY || 0)),
              };
          desired = blendPoints(desired, screenTarget, progress > 0.62 ? 0.2 : 0.1);
        }

        if (role !== 'RETURNER') {
          const lateralSign = role === 'SHIELD'
            ? -cutDirection
            : role === leadContainRole
              ? cutDirection
              : role === backsideContainRole
                ? -cutDirection
                : role === forceGunnerRole
                  ? cutDirection
                  : role === cleanupGunnerRole
                    ? -cutDirection
                    : secondaryCoverageRoleSet.has(role)
                      ? Math.sign((plan.targetOffsetY || finishSideForRole(role) * 12) || 1)
                      : escortBlockRoleSet.has(role)
                        ? Math.sign(plan.screenOffsetY || (state.y - projectedCarrier.y) || cutDirection || 1)
                        : 0;
          if (lateralSign !== 0) {
            desired = curvedApproachTarget(state, desired, lateralSign, {
              nearDistance: role.startsWith('GUNNER') ? 20 : escortBlockRoleSet.has(role) ? 18 : 22,
              farDistance: role.startsWith('GUNNER') ? 116 : secondaryCoverageRoleSet.has(role) ? 98 : 92,
              minOffset: escortBlockRoleSet.has(role) ? 3 : 4,
              maxOffset: role === forceGunnerRole || role === cleanupGunnerRole
                ? 16
                : secondaryCoverageRoleSet.has(role)
                  ? 12
                  : escortBlockRoleSet.has(role)
                    ? 10
                    : 9,
              scale: role.startsWith('GUNNER') ? 1.06 : escortBlockRoleSet.has(role) ? 0.9 : 1,
            });
          }
        }

        const isSecondaryCoverage = secondaryCoverageRoleSet.has(role);
        const isEscortBlock = escortBlockRoleSet.has(role);
        const previousReturnerPoint = role === 'RETURNER'
          ? { x: state.x, y: state.y }
          : null;
        steerMotionState(state, desired, {
          family: plan.family,
          scale: plan.scale,
          accelRatio: role === 'RETURNER'
            ? 0.27
            : role.startsWith('GUNNER')
              ? 0.21
              : isSecondaryCoverage
                ? 0.18
                : isEscortBlock
                  ? 0.17
                  : 0.19,
          drag: role.startsWith('GUNNER')
            ? 0.89
            : isSecondaryCoverage
              ? 0.9
              : isEscortBlock
                ? 0.91
                : 0.92,
          inertia: role === 'RETURNER'
            ? 0.988
            : role.startsWith('GUNNER')
              ? 0.96
              : isSecondaryCoverage
                ? 0.965
                : 0.97,
          arriveDistance: role === 'RETURNER'
            ? 20
            : role.startsWith('GUNNER')
              ? 24
              : isSecondaryCoverage
                ? 22
                : isEscortBlock
                  ? 18
                  : 20,
          stopDistance: role === 'RETURNER' ? 4 : isEscortBlock ? 4 : 5,
        });
        if (previousReturnerPoint) {
          const maxReturnerStep = Math.max(
            6,
            playerPixelsPerFrame(state.speedRating || 84, plan.family || 'run', plan.scale ?? 1) * 1.55,
          );
          const returnerStep = pointDistance(previousReturnerPoint, state);
          if (returnerStep > maxReturnerStep) {
            const cappedReturnerPoint = movePointTowards(previousReturnerPoint, state, maxReturnerStep);
            state.x = cappedReturnerPoint.x;
            state.y = cappedReturnerPoint.y;
            state.vx = cappedReturnerPoint.x - previousReturnerPoint.x;
            state.vy = cappedReturnerPoint.y - previousReturnerPoint.y;
          }
        }
      });

      secondaryCoverageEntries.forEach((entry: any, index: number) => {
        const sameSide = entry.side === cutDirection;
        applyContest(entry.role, coverageMatchByRole.get(entry.role), returnProgress, {
          side: entry.side,
          engageDistance: entry.kind === 'upback' ? 34 : 32 + index * 2,
          breakthroughProgress: entry.kind === 'upback'
            ? 0.54
            : sameSide
              ? 0.28 + index * 0.05 + (playHash(context.play, 'punt-breakthrough-' + entry.role) % 2) * 0.03
              : 0.46 + index * 0.08 + (playHash(context.play, 'punt-breakthrough-' + entry.role) % 3) * 0.04,
          holdStrength: entry.kind === 'upback' ? 0.86 : sameSide ? 0.72 - index * 0.04 : 0.9 - index * 0.05,
          coverageDrag: entry.kind === 'upback' ? 0.72 : sameSide ? 0.62 - index * 0.03 : 0.82 - index * 0.04,
          blockerDrag: 0.18,
          coverageOffsetX: entry.kind === 'upback' ? 6 : 4 + index,
          coverageOffsetY: entry.kind === 'upback' ? 6 : 8 + index * 2,
          blockerOffsetX: 10 + index * 2,
          blockerOffsetY: 6 + index * 2,
        });
      });

      [
        {
          coverageRole: forceGunnerRole,
          blockerRole: leadContainRole,
          side: cutDirection,
          breakthroughProgress: 0.28 + hangRatio * 0.08,
        },
        {
          coverageRole: cleanupGunnerRole,
          blockerRole: backsideContainRole,
          side: -cutDirection,
          breakthroughProgress: 0.36 + hangRatio * 0.08,
        },
      ].forEach(({ coverageRole, blockerRole, side, breakthroughProgress }) => {
        applyContest(coverageRole, blockerRole, returnProgress, {
          side,
          engageDistance: 30,
          breakthroughProgress,
          holdStrength: 0.74,
          coverageDrag: 0.68,
          blockerDrag: 0.2,
          coverageOffsetX: 8,
          coverageOffsetY: 8,
          blockerOffsetX: 12,
          blockerOffsetY: 8,
        });
      });

      dynamicOrder.forEach((role) => {
        const plan = dynamicPlans.get(role);
        const state = dynamicStates.get(role);
        if (!plan || !state) {
          return;
        }
        const targetPlayers = plan.team === 'offense' ? offensePlayers : defensePlayers;
        setPlayerState(targetPlayers, role, {
          x: state.x,
          y: state.y,
          moving: Math.hypot(state.vx || 0, state.vy || 0) > 0.16,
        });
      });
    }

    const returner = findPlayer(defensePlayers, 'RETURNER');
    const punter = findPlayer(offensePlayers, 'PUNTER');
    let ballX;
    let ballY;
    if (frame <= snapTravelFrames) {
      const snapProgress = clamp01(frame / Math.max(1, snapTravelFrames));
      ballX = snapToPixel(lerp(snapOrigin.ballX, snapTarget.ballX, snapProgress));
      ballY = snapToPixel(lerp(snapOrigin.ballY, snapTarget.ballY, snapProgress) - Math.sin(snapProgress * Math.PI) * 18);
    } else if (frame < puntReleaseFrame) {
      const ballPoint = puntCarryBallPoint(punter, context.direction, snapTarget.ballX, snapTarget.ballY);
      ballX = ballPoint.ballX;
      ballY = ballPoint.ballY;
    } else if (frame <= flightEnd) {
      const ballProgress = clamp01((frame - puntReleaseFrame) / Math.max(1, flightEnd - puntReleaseFrame));
      const flightTarget = touchback
        ? { x: screenXForYard(100, context.direction) + 6, y: context.laneY + 8 }
        : { x: catchPoint.x + 6, y: catchPoint.y + 8 };
      ballX = snapToPixel(lerp(puntOrigin.ballX, flightTarget.x, ballProgress));
      ballY = snapToPixel(lerp(puntOrigin.ballY, flightTarget.y, ballProgress) - Math.sin(ballProgress * Math.PI) * (118 + hangRatio * 22));
    } else {
      const ballPoint = carriedBallPoint(returner, catchPoint.x, catchPoint.y, -context.direction);
      ballX = ballPoint.ballX;
      ballY = ballPoint.ballY;
    }

    if (frame === lastTrackedFrame && !tackledReturn) {
      setPlayersStill(offensePlayers, defensePlayers);
    }

    frames.push({ offensePlayers, defensePlayers, ball: { ballX, ballY } });
  }

  if (!tackledReturn || !frames.length) {
    return smoothPuntReturnerFrames(frames, context.direction);
  }

  let carryFrame = cloneLiveFrame(frames[frames.length - 1]);
  let frozenFrame: any = null;
  const finishSupportRoles = finishRoles.filter((role) => role !== primaryFinishRole);
  const coverageGapForRole = (players: any[], role: string | null, target: any) => {
    if (!role) {
      return Infinity;
    }
    const player = findPlayer(players, role);
    return player ? pointDistance(player, target) : Infinity;
  };
  const initialReturner = findPlayer(carryFrame.defensePlayers, 'RETURNER');
  const primaryFinishPlayer = primaryFinishRole
    ? findPlayer(carryFrame.offensePlayers, primaryFinishRole) || findPlayer(offenseBase, primaryFinishRole)
    : null;
  const initialPrimaryGap = initialReturner ? coverageGapForRole(carryFrame.offensePlayers, primaryFinishRole, initialReturner) : 28;
  const finishPace = playerPixelsPerFrame(
    primaryFinishPlayer?.speedRating || 78,
    'run',
    shortReturnFinish ? 0.74 : mediumReturnFinish ? 0.8 : 0.84,
  );
  const maxFinishFrames = Math.max(
    shortReturnFinish ? 18 : mediumReturnFinish ? 20 : 22,
    Math.round(initialPrimaryGap / Math.max(1, finishPace)) + 10,
  );

  for (let extraFrame = 0; extraFrame < maxFinishFrames && !frozenFrame; extraFrame += 1) {
    const offenseFrame = clonePlayers(carryFrame.offensePlayers);
    const defenseFrame = clonePlayers(carryFrame.defensePlayers);
    const returner = findPlayer(defenseFrame, 'RETURNER');
    if (!returner) {
      break;
    }

    const currentPrimaryGap = coverageGapForRole(carryFrame.offensePlayers, primaryFinishRole, returner);
    const preContactSpot = {
      x: snapToPixel(returnSpot.x + xDelta(shortReturnFinish ? 4 : mediumReturnFinish ? 8 : 4)),
      y: returnSpot.y,
    };
    const contactBlend = clamp01(
      shortReturnFinish
        ? 1 - (currentPrimaryGap - 4) / 36
        : 1 - (currentPrimaryGap - (mediumReturnFinish ? 7 : 8)) / (mediumReturnFinish ? 48 : 30)
    );
    const returnerTarget = blendPoints(preContactSpot, returnSpot, contactBlend);
    const returnerNext = movePointTowards(
      { x: returner.x, y: returner.y },
      returnerTarget,
      playerPixelsPerFrame(returner.speedRating || 84, 'run', 0.88),
    );
    const reachedSpot = spotReached(returnerNext, returnSpot, 5);
    const closeWindow = spotReached(returnerNext, returnSpot, shortReturnFinish ? 24 : mediumReturnFinish ? 18 : 14);
    const deadBallSettled = spotReached(returnerNext, returnSpot, 2);
    setPlayerState(defenseFrame, 'RETURNER', {
      x: returnerNext.x,
      y: returnerNext.y,
      moving: !deadBallSettled,
    });

    const shield = findPlayer(defenseFrame, 'SHIELD');
    if (shield) {
      if (deadBallSettled) {
        setPlayerState(defenseFrame, 'SHIELD', { x: shield.x, y: shield.y, moving: false });
      } else {
      const shieldNext = movePointTowards(
        { x: shield.x, y: shield.y },
        {
          x: snapToPixel(returnerNext.x + returnXDelta(14)),
          y: clampFieldY(returnerNext.y - cutDirection * 8),
        },
        playerPixelsPerFrame(shield.speedRating || 72, 'run', 0.4),
      );
      setPlayerState(defenseFrame, 'SHIELD', { x: shieldNext.x, y: shieldNext.y, moving: true });
      }
    }

    [
      { role: leadContainRole, offsetX: 18, offsetY: cutDirection * 12, scale: 0.5 },
      { role: backsideContainRole, offsetX: 8, offsetY: -cutDirection * 18, scale: 0.44 },
    ].forEach(({ role, offsetX, offsetY, scale }) => {
      const contain = findPlayer(defenseFrame, role);
      if (!contain) {
        return;
      }
      if (deadBallSettled) {
        setPlayerState(defenseFrame, role, { x: contain.x, y: contain.y, moving: false });
        return;
      }
      const containNext = movePointTowards(
        { x: contain.x, y: contain.y },
        {
          x: snapToPixel(returnerNext.x + returnXDelta(offsetX)),
          y: clampFieldY(returnerNext.y + offsetY),
        },
        playerPixelsPerFrame(contain.speedRating || 76, 'run', scale),
      );
      setPlayerState(defenseFrame, role, { x: containNext.x, y: containNext.y, moving: true });
    });

    interiorBlockEntries.forEach((entry: any, index: number) => {
      const blocker = findPlayer(defenseFrame, entry.role);
      if (!blocker) {
        return;
      }
      if (deadBallSettled) {
        setPlayerState(defenseFrame, entry.role, { x: blocker.x, y: blocker.y, moving: false });
        return;
      }
      if (extraFrame < ((escortBlockPlans.get(entry.role)?.finishDelay) ?? index + 1)) {
        return;
      }
      const plan = escortBlockPlans.get(entry.role);
      const blockerNext = movePointTowards(
        { x: blocker.x, y: blocker.y },
        {
          x: snapToPixel(returnerNext.x + returnXDelta(plan?.screenOffsetX || (18 + index * 4))),
          y: clampFieldY(returnerNext.y + (plan?.screenOffsetY || entry.side * (12 + index * 5))),
        },
        playerPixelsPerFrame(blocker.speedRating || 74, 'run', plan?.finishScale || 0.34),
      );
      setPlayerState(defenseFrame, entry.role, { x: blockerNext.x, y: blockerNext.y, moving: true });
    });
    [
      {
        role: forceGunnerRole,
        delay: 1,
        stalkOffsetX: 22,
        stalkOffsetY: cutDirection * 18,
        closeOffsetX: 1,
        closeOffsetY: cutDirection * 4,
        scale: 0.56,
        closeScale: 0.46,
      },
      {
        role: cleanupGunnerRole,
        delay: 5,
        stalkOffsetX: 30,
        stalkOffsetY: -cutDirection * 12,
        closeOffsetX: 6,
        closeOffsetY: -cutDirection * 6,
        scale: 0.52,
        closeScale: 0.42,
      },
    ].forEach(({ role, delay, stalkOffsetX, stalkOffsetY, closeOffsetX, closeOffsetY, scale, closeScale }) => {
      const gunner = findPlayer(offenseFrame, role);
      const finishRank = finishRankForRole(role);
      const activeDelay = Math.max(
        0,
        delay
          + (finishRank === 0 ? -1 : finishRank === 1 ? 1 : 5)
          - Math.round(coverageCompression * (finishRank <= 1 ? 2 : 0))
          - (shortReturnFinish ? (finishRank <= 1 ? 2 : 0) : mediumReturnFinish ? (finishRank === 0 ? 1 : 0) : 0),
      );
      if (!gunner || extraFrame < activeDelay) {
        return;
      }
      const gunnerGap = pointDistance(gunner, returnerNext);
      if (deadBallSettled && finishRank > 1 && gunnerGap > 110) {
        setPlayerState(offenseFrame, role, { x: gunner.x, y: gunner.y, moving: false });
        return;
      }
      const targetOffsetX = closeWindow
        ? finishRank === 0
          ? Math.max(0, closeOffsetX - (shortReturnFinish ? 4 : 2))
          : finishRank === 1
            ? closeOffsetX + (shortReturnFinish ? 1 : 3)
            : stalkOffsetX + 12
        : finishRank === 0
          ? Math.max(shortReturnFinish ? 4 : 8, stalkOffsetX - Math.round(coverageCompression * (shortReturnFinish ? 14 : 10)))
          : finishRank === 1
            ? stalkOffsetX + (shortReturnFinish ? 0 : 2)
            : stalkOffsetX + 18;
      const targetOffsetY = closeWindow
        ? finishRank === 0
          ? closeOffsetY
          : finishRank === 1
            ? closeOffsetY + (role === cleanupGunnerRole ? -cutDirection * 2 : cutDirection * 2)
            : stalkOffsetY
        : finishRank === 0
          ? stalkOffsetY - Math.sign(stalkOffsetY || cutDirection) * Math.round(coverageCompression * 4)
          : finishRank === 1
            ? stalkOffsetY
            : stalkOffsetY + (role === cleanupGunnerRole ? -cutDirection * 6 : cutDirection * 6);
      const activeScale = closeWindow
        ? Math.max(
            0.26,
            closeScale
              + (finishRank === 0 ? (shortReturnFinish ? 0.18 : 0.08) : finishRank === 1 ? 0.02 : -0.08)
              + (shortReturnFinish && finishRank === 0 ? 0.06 : 0)
              + (shortReturnFinish ? 0.08 : mediumReturnFinish ? 0.04 : 0),
          )
        : Math.max(
            0.28,
            scale
              + (finishRank === 0 ? 0.08 : finishRank === 1 ? 0.02 : -0.1)
              + (shortReturnFinish ? 0.12 : mediumReturnFinish ? 0.05 : 0),
          );
      const gunnerNext = movePointTowards(
        { x: gunner.x, y: gunner.y },
        {
          x: snapToPixel(returnerNext.x + xDelta(targetOffsetX)),
          y: clampFieldY(returnerNext.y + targetOffsetY),
        },
        playerPixelsPerFrame(gunner.speedRating || 84, 'pass', activeScale),
      );
      setPlayerState(offenseFrame, role, { x: gunnerNext.x, y: gunnerNext.y, moving: true });
    });

    secondaryCoverageEntries.forEach((entry: any, index: number) => {
      const coveragePlayer = findPlayer(offenseFrame, entry.role);
      const plan = secondaryCoveragePlans.get(entry.role);
      const finishRank = finishRankForRole(entry.role);
      const activeDelay = Math.max(
        0,
        (plan?.finishDelay ?? (index + 2))
          + (finishRank === 0 ? -2 : finishRank === 1 ? -1 : finishRank === 2 ? 1 : 5)
          - Math.round(coverageCompression * (finishRank <= 1 ? 2 : 0)),
      );
      if (!coveragePlayer) {
        return;
      }
      const coverageGap = pointDistance(coveragePlayer, returnerNext);
      if (deadBallSettled && finishRank > 1 && coverageGap > 110) {
        setPlayerState(offenseFrame, entry.role, { x: coveragePlayer.x, y: coveragePlayer.y, moving: false });
        return;
      }
      if (extraFrame < activeDelay) {
        return;
      }
      const sameSide = entry.side === cutDirection;
      const shedNow = extraFrame >= activeDelay + (sameSide ? 4 : 8);
      const baseCloseOffsetX = plan?.targetOffsetX || (18 + index * 4);
      const baseCloseOffsetY = plan?.targetOffsetY || (entry.side * (8 + index * 4));
      const closeOffsetX = finishRank === 0
        ? Math.max(4, baseCloseOffsetX - 12)
        : finishRank === 1
          ? Math.max(8, baseCloseOffsetX - 6)
          : shedNow
            ? Math.max(8, baseCloseOffsetX - (sameSide ? 8 : 4))
            : baseCloseOffsetX;
      const closeOffsetY = finishRank === 0
        ? baseCloseOffsetY - entry.side * 6
        : finishRank === 1
          ? baseCloseOffsetY - entry.side * 3
          : shedNow
            ? baseCloseOffsetY - entry.side * 4
            : baseCloseOffsetY;
      const stalkOffsetX = closeOffsetX + 12;
      const stalkOffsetY = closeOffsetY + (finishRank === 0
        ? entry.side * 2
        : entry.side === cutDirection ? cutDirection * 6 : -cutDirection * 6);
      const activeScale = closeWindow
        ? Math.min(0.78, (plan?.finishScale || 0.38) + (finishRank === 0 ? 0.18 : finishRank === 1 ? 0.1 : shedNow ? 0.12 : 0))
        : Math.min(0.7, (plan?.finishScale || 0.38) + (finishRank === 0 ? 0.2 : finishRank === 1 ? 0.12 : shedNow ? 0.14 : 0.08));
      const coverageNext = movePointTowards(
        { x: coveragePlayer.x, y: coveragePlayer.y },
        {
          x: snapToPixel(returnerNext.x + xDelta(closeWindow ? closeOffsetX : stalkOffsetX)),
          y: clampFieldY(returnerNext.y + (closeWindow ? closeOffsetY : stalkOffsetY)),
        },
        playerPixelsPerFrame(coveragePlayer.speedRating || 76, 'pass', activeScale),
      );
      setPlayerState(offenseFrame, entry.role, { x: coverageNext.x, y: coverageNext.y, moving: true });
    });

    const primaryGap = coverageGapForRole(offenseFrame, primaryFinishRole, returnerNext);
    const nearestSupportGap = [...finishSupportRoles, forceGunnerRole, cleanupGunnerRole, ...secondaryCoverageEntries.map((entry: any) => entry.role)]
      .filter((role: string, index: number, roles: string[]) => role !== primaryFinishRole && roles.indexOf(role) === index)
      .reduce((minGap: number, role: string) => Math.min(minGap, coverageGapForRole(offenseFrame, role, returnerNext)), Infinity);
    const nearbyCoverageCount = [forceGunnerRole, cleanupGunnerRole, ...secondaryCoverageEntries.map((entry: any) => entry.role)]
      .filter((role: string, index: number, roles: string[]) => roles.indexOf(role) === index)
      .reduce((count: number, role: string) =>
        count + (coverageGapForRole(offenseFrame, role, returnerNext) <= 110 ? 1 : 0), 0);
    const nearbyInteriorCount = secondaryCoverageEntries.reduce((count: number, entry: any) =>
      count + (coverageGapForRole(offenseFrame, entry.role, returnerNext) <= 88 ? 1 : 0), 0);
    const primaryGapLimit = shortReturnFinish
      ? (primaryFinishIsInterior ? 10 : 12)
      : mediumReturnFinish
        ? (primaryFinishIsInterior ? 11 : 13)
        : (primaryFinishIsInterior ? 10 : 12);
    const supportGapLimit = shortReturnFinish
      ? 80
      : mediumReturnFinish
        ? 56
        : (primaryFinishIsInterior ? 32 : 42);
    const requiredCoverageCount = shortReturnFinish ? 1 : mediumReturnFinish ? 2 : 3;
    const requiredInteriorCount = shortReturnFinish ? 0 : mediumReturnFinish ? 0 : 1;
    const closeEnough = reachedSpot
      && primaryGap <= primaryGapLimit
      && nearestSupportGap <= supportGapLimit
      && nearbyCoverageCount >= requiredCoverageCount
      && nearbyInteriorCount >= requiredInteriorCount;
    if (deadBallSettled && !closeEnough) {
      setPlayerState(defenseFrame, 'RETURNER', {
        x: returnerNext.x,
        y: returnerNext.y,
        moving: true,
      });
    }
    const ball = carriedBallPoint(findPlayer(defenseFrame, 'RETURNER'), returnSpot.x, returnSpot.y, -context.direction);
    const liveFrame = closeEnough
      ? freezeLiveFrame(offenseFrame, defenseFrame, ball)
      : { offensePlayers: offenseFrame, defensePlayers: defenseFrame, ball };

    if (closeEnough) {
      frozenFrame = liveFrame;
    }

    frames.push(liveFrame);
    carryFrame = liveFrame;
  }

  if (!frozenFrame) {
    frames.push(freezeLiveFrame(
      clonePlayers(carryFrame.offensePlayers),
      clonePlayers(carryFrame.defensePlayers),
      { ...carryFrame.ball },
    ));
  }

  return smoothPuntReturnerFrames(frames, context.direction);
}
